import { SEO } from '../../utils/seo';

export default function UseCasePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '導入メリット - Use Case / DX効果',
    description: '自治体・医療機関・企業向けの具体的な導入効果。住民サービスのデジタル化、医療データの一元管理、診療業務の効率化、従業員の健康管理DXなど、実際の導入メリットをご紹介します。',
    url: `${siteUrl}/usecase`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: [
        {
          '@type': 'Article',
          headline: '自治体向け導入メリット',
          description: '住民サービスのデジタル化、医療・介護データの一元管理、高齢者の見守り・生活支援により、住民の利便性向上と業務効率化を実現。'
        },
        {
          '@type': 'Article',
          headline: '医療機関向け導入メリット',
          description: '診療業務の効率化、患者データの安全な共有、遠隔医療・オンライン診療により、医療の質向上と業務負担軽減を実現。'
        },
        {
          '@type': 'Article',
          headline: '企業向け導入メリット',
          description: '従業員の健康管理DX、サプライチェーンの透明化、施設・インフラの長寿命化により、生産性向上とコスト削減を実現。'
        }
      ]
    }
  };

  const useCases = [
    {
      target: '自治体向け',
      icon: 'ri-government-line',
      benefits: [
        {
          title: '住民サービスのデジタル化',
          description: 'AIによる24時間対応の問い合わせ窓口、オンライン申請・予約システムで、住民の利便性向上と窓口業務の効率化を実現。',
          image: 'https://public.readdy.ai/ai/img_res/3b45991d3da9b985103981cce6b7db26.jpg'
        },
        {
          title: '医療・介護データの一元管理',
          description: 'ブロックチェーンベースの健康情報システムにより、診療歴・薬歴・介護履歴を安全に管理。地域包括ケアの質を向上。',
          image: 'https://readdy.ai/api/search-image?query=Secure%20healthcare%20data%20management%20system%20on%20computer%20screens%2C%20medical%20records%20and%20patient%20information%20protected%20by%20blockchain%20technology%2C%20Japanese%20hospital%20or%20clinic%20setting%2C%20professional%20healthcare%20IT%20environment%20with%20blue%20interface&width=600&height=400&seq=usecase-002&orientation=landscape'
        },
        {
          title: '高齢者の見守り・生活支援',
          description: '音声操作可能なAIアプリで、高齢者の服薬管理・健康チェック・緊急時対応をサポート。孤立防止にも貢献。',
          image: 'https://readdy.ai/api/search-image?query=Elderly%20Japanese%20person%20using%20voice-activated%20AI%20assistant%20at%20home%2C%20smart%20speaker%20and%20tablet%20showing%20health%20monitoring%20interface%2C%20comfortable%20living%20room%20with%20caring%20technology%20support%2C%20warm%20natural%20lighting%20with%20blue%20digital%20elements&width=600&height=400&seq=usecase-003&orientation=landscape'
        }
      ]
    },
    {
      target: '医療機関向け',
      icon: 'ri-hospital-line',
      benefits: [
        {
          title: '診療業務の効率化',
          description: 'AIによる問診補助、予約管理の自動化により、医師・看護師の負担を軽減し、本来の医療行為に集中できる環境を実現。',
          image: 'https://readdy.ai/api/search-image?query=Modern%20Japanese%20hospital%20with%20AI-assisted%20medical%20consultation%2C%20doctor%20using%20digital%20tablet%20for%20patient%20diagnosis%2C%20efficient%20healthcare%20workflow%20with%20technology%20integration%2C%20clean%20medical%20facility%20with%20blue%20and%20white%20color%20scheme&width=600&height=400&seq=usecase-004&orientation=landscape'
        },
        {
          title: '患者データの安全な共有',
          description: 'ブロックチェーン技術により、患者同意のもと、複数医療機関間で診療情報を安全に共有。重複検査の削減と診療の質向上。',
          image: 'https://readdy.ai/api/search-image?query=Secure%20medical%20data%20sharing%20network%20between%20hospitals%2C%20blockchain-protected%20patient%20records%20transfer%2C%20healthcare%20professionals%20collaborating%20through%20digital%20platform%2C%20professional%20medical%20IT%20infrastructure%20with%20blue%20security%20indicators&width=600&height=400&seq=usecase-005&orientation=landscape'
        },
        {
          title: '遠隔医療・オンライン診療',
          description: 'AIアプリを通じた事前問診と、安全なビデオ通話システムで、地方在住者や通院困難な患者への医療アクセスを改善。',
          image: 'https://readdy.ai/api/search-image?query=Telemedicine%20consultation%20with%20elderly%20patient%20at%20home%20using%20tablet%2C%20doctor%20providing%20remote%20healthcare%20service%20through%20video%20call%2C%20comfortable%20home%20setting%20with%20reliable%20digital%20connection%2C%20warm%20and%20reassuring%20atmosphere%20with%20blue%20interface&width=600&height=400&seq=usecase-006&orientation=landscape'
        }
      ]
    },
    {
      target: '企業向け',
      icon: 'ri-building-line',
      benefits: [
        {
          title: '従業員の健康管理DX',
          description: 'AIアプリによる健康チェック・ストレス管理で、従業員の健康維持と生産性向上を支援。産業医との連携も可能。',
          image: 'https://readdy.ai/api/search-image?query=Corporate%20wellness%20program%20with%20employees%20using%20health%20management%20app%2C%20office%20workers%20checking%20health%20metrics%20on%20smartphones%2C%20modern%20Japanese%20workplace%20with%20digital%20health%20monitoring%2C%20professional%20business%20environment%20with%20blue%20wellness%20interface&width=600&height=400&seq=usecase-007&orientation=landscape'
        },
        {
          title: 'サプライチェーンの透明化',
          description: 'ブロックチェーンによるトレーサビリティで、輸入商品の品質・産地情報を確実に管理。消費者への信頼性向上。',
          image: 'https://readdy.ai/api/search-image?query=Supply%20chain%20transparency%20with%20blockchain%20tracking%2C%20coffee%20beans%20journey%20from%20Indonesian%20farm%20to%20Japanese%20consumer%2C%20digital%20traceability%20system%20showing%20product%20origin%2C%20clean%20infographic%20style%20with%20blue%20connection%20lines&width=600&height=400&seq=usecase-008&orientation=landscape'
        },
        {
          title: '施設・インフラの長寿命化',
          description: '防水コンクリート等の新技術導入により、建物・設備のメンテナンスコストを削減。長期的な資産価値を保全。',
          image: 'https://readdy.ai/api/search-image?query=Modern%20commercial%20building%20with%20advanced%20waterproof%20concrete%20technology%2C%20infrastructure%20maintenance%20with%20innovative%20materials%2C%20durable%20construction%20ensuring%20long-term%20asset%20value%2C%20professional%20architectural%20photography%20with%20blue%20sky%20background&width=600&height=400&seq=usecase-009&orientation=landscape'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="導入メリット｜福岡自治体医療機関企業DX効果"
        description="自治体・医療機関・企業向けの具体的な導入効果。住民サービスのデジタル化、医療データの一元管理、診療業務の効率化、従業員の健康管理DXなど、実際の導入メリットをご紹介します。"
        keywords="導入メリット,DX効果,自治体,医療機関,企業,デジタル化,業務効率化"
        schema={schema}
      />
      {/* Page Header */}
      <div className="relative bg-gradient-to-br from-blue-900 to-blue-700 text-white py-32 px-6 md:px-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Digital%20transformation%20success%20stories%20in%20Japanese%20municipalities%20and%20healthcare%2C%20smart%20city%20implementation%20with%20happy%20citizens%20using%20technology%2C%20modern%20public%20services%20innovation%2C%20optimistic%20future%20vision%20with%20blue%20and%20white%20tones&width=1920&height=600&seq=usecase-header-001&orientation=landscape"
            alt="Use Case Background"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-widest uppercase mb-4 text-blue-200">Use Case / DX効果</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">導入メリット</h1>
          <p className="text-lg text-blue-100 leading-relaxed">
            自治体・医療機関・企業向けの具体的な導入効果
          </p>
        </div>
      </div>

      {/* Use Cases */}
      <section className="py-20 md:py-24 px-6 md:px-20">
        <div className="max-w-7xl mx-auto space-y-32">
          {useCases.map((useCase, index) => (
            <div key={index}>
              {/* Target Header */}
              <div className="text-center mb-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6">
                  <i className={`${useCase.icon} text-3xl text-blue-600`}></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-blue-900">
                  {useCase.target}
                </h2>
              </div>

              {/* Benefits Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                {useCase.benefits.map((benefit, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 group"
                  >
                    {/* Image */}
                    <div className="relative w-full h-56 overflow-hidden">
                      <img
                        src={benefit.image}
                        alt={benefit.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent"></div>
                    </div>

                    {/* Content */}
                    <div className="p-8">
                      <h3 className="text-xl font-bold text-blue-900 mb-4 leading-tight">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-16 px-6 md:px-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            導入のご相談はお気軽に
          </h2>
          <p className="text-lg text-blue-100 mb-8 leading-relaxed">
            貴組織の課題に合わせた最適なソリューションをご提案いたします
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center whitespace-nowrap px-12 py-4 bg-white text-blue-900 rounded-lg font-medium hover:shadow-lg transition-all duration-300 cursor-pointer"
          >
            お問い合わせ
          </a>
        </div>
      </section>
    </div>
  );
}
